/**
 * Klantstem — Bouwbrief 07.
 *
 * Wisselen tussen de ingevulde stemmen, met de bolletjes of automatisch.
 *
 * Automatisch doorschuiven (16 sep, Sjoerd): elke data-interval seconden de
 * volgende stem. Omdat lezers niet halverwege een zin weggeschoven mogen
 * worden (WCAG 2.2.2), pauzeert de klok zolang de muis boven het blok staat
 * of de focus erin zit, en loopt hij niet als het blok buiten beeld is, het
 * tabblad verborgen is of de bezoeker "beweging beperken" aan heeft. Een klik
 * op een bolletje zet de klok opnieuw op nul.
 */

class MivozaVoice extends HTMLElement {
  #timer = 0;
  #pauze = false;
  #inBeeld = false;
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onKlik);

    const seconden = Number(this.dataset.interval) || 0;
    if (seconden <= 0 || this.#totaal() < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.interval = seconden * 1000;

    this.addEventListener('mouseenter', this.#pauzeer);
    this.addEventListener('mouseleave', this.#hervat);
    this.addEventListener('focusin', this.#pauzeer);
    this.addEventListener('focusout', this.#onFocusUit);
    document.addEventListener('visibilitychange', this.#plan);

    this.#observer = new IntersectionObserver((entries) => {
      this.#inBeeld = entries.some((e) => e.isIntersecting);
      this.#plan();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onKlik);
    this.removeEventListener('mouseenter', this.#pauzeer);
    this.removeEventListener('mouseleave', this.#hervat);
    this.removeEventListener('focusin', this.#pauzeer);
    this.removeEventListener('focusout', this.#onFocusUit);
    document.removeEventListener('visibilitychange', this.#plan);
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  #totaal() {
    return this.querySelectorAll('[data-voice-item]').length;
  }

  #huidig() {
    const zichtbaar = this.querySelector('[data-voice-item]:not([hidden])');
    return zichtbaar ? Number(zichtbaar.dataset.voiceItem) : 1;
  }

  #plan = () => {
    clearTimeout(this.#timer);
    if (!this.interval || this.#pauze || !this.#inBeeld || document.hidden) return;
    this.#timer = setTimeout(() => {
      this.#toon((this.#huidig() % this.#totaal()) + 1);
      this.#plan();
    }, this.interval);
  };

  #pauzeer = () => {
    this.#pauze = true;
    clearTimeout(this.#timer);
  };

  #hervat = () => {
    if (this.contains(document.activeElement)) return;
    this.#pauze = false;
    this.#plan();
  };

  #onFocusUit = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (this.matches(':hover')) return;
    this.#hervat();
  };

  #onKlik = (event) => {
    const knop = event.target instanceof Element ? event.target.closest('[data-voice-dot]') : null;
    if (!knop) return;
    this.#toon(Number(knop.dataset.voiceDot));
    this.#plan();
  };

  #toon(nummer) {
    for (const item of this.querySelectorAll('[data-voice-item]')) {
      item.hidden = Number(item.dataset.voiceItem) !== nummer;
    }

    for (const dot of this.querySelectorAll('[data-voice-dot]')) {
      const aan = Number(dot.dataset.voiceDot) === nummer;
      dot.classList.toggle('is-on', aan);
      // aria-current en niet aria-selected: dit zijn losse knoppen, geen tabs.
      if (aan) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('mivoza-voice')) {
  customElements.define('mivoza-voice', MivozaVoice);
}
